import { FC, useEffect, useMemo, useState } from 'react'

import { StreamViews } from '@img-arena/consoles-api-streaming'
import { Pagination, TableBase } from '@/components/table/base'
import { streamingRestApi } from '@/service/streamingApi'

import { StatisticsCard, StatisticsCardSkeleton } from '../../StatisticsCard'
import { STATISTICS_CSV_HEADERS } from '../constants'
import { ComponentWithStatisticsQueryParams } from '../types'
import { EVENT_STREAMS_COLUMNS } from './eventStreams.table'

const CSV_HEADERS = [
  STATISTICS_CSV_HEADERS.OPERATOR_NAME,
  STATISTICS_CSV_HEADERS.TOTAL_VIEWS,
  STATISTICS_CSV_HEADERS.TOTAL_BYTES,
  STATISTICS_CSV_HEADERS.TOTAL_DURATON,
  STATISTICS_CSV_HEADERS.AVERAGE_DURATION,
  STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS,
  STATISTICS_CSV_HEADERS.UNIQUE_VIEWS
]

export const EVENT_STREAMS_CARD_TEST_ID = 'event-streams-card' as const
export const EVENT_STREAMS_SKELETON_TEST_ID = 'event-streams-skeleton' as const

export const EventStreams: FC<ComponentWithStatisticsQueryParams> = ({ queryParams }) => {
  const analyticsEndpoints = streamingRestApi.endpoints.analytics

  const [streamViewsData, setStreamViewsData] = useState<StreamViews | null>(null)
  const streamViews = streamViewsData?.views

  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    pageSize: 10,
    total: streamViewsData?.totalEntries || 0
  })

  const { page, pageSize } = pagination

  useEffect(() => {
    const ac = new AbortController()

    analyticsEndpoints
      .getStreamViews({ ...queryParams, page, pageSize }, ac.signal)
      .then((data) => {
        setStreamViewsData(data)
      })

    return () => {
      ac.abort()
    }
  }, [...Object.values(queryParams), page, pageSize])

  useEffect(() => {
    if (!streamViewsData?.totalEntries) return

    setPagination((oldPagination) => ({
      ...oldPagination,
      total: streamViewsData.totalEntries
    }))
  }, [streamViewsData?.totalEntries])

  const handleSetPagination = (newPagination: Omit<Pagination, 'total'>) => {
    setPagination((oldPagination) => ({
      ...oldPagination,
      ...newPagination
    }))
  }

  const csvData = useMemo(() => {
    if (!streamViews?.length) return

    const data = streamViews.map(
      ({ streamName, totalViews, bytesSent, totalDuration, averageDuration, uniqueViews }) => ({
        [STATISTICS_CSV_HEADERS.STREAM_NAME]: streamName,
        [STATISTICS_CSV_HEADERS.TOTAL_VIEWS]: totalViews,
        [STATISTICS_CSV_HEADERS.TOTAL_BYTES]: bytesSent,
        [STATISTICS_CSV_HEADERS.TOTAL_DURATON]: totalDuration,
        [STATISTICS_CSV_HEADERS.AVERAGE_DURATION]: averageDuration,
        [STATISTICS_CSV_HEADERS.UNIQUE_VIEWS]: uniqueViews
      })
    )

    return {
      csvHeaders: CSV_HEADERS,
      data
    }
  }, [streamViews])

  if (!streamViews) {
    return <StatisticsCardSkeleton testId={EVENT_STREAMS_SKELETON_TEST_ID} height={600} />
  }

  return (
    <StatisticsCard title="Event Streams" testId={EVENT_STREAMS_CARD_TEST_ID} csvData={csvData}>
      <TableBase
        dense
        columns={EVENT_STREAMS_COLUMNS}
        data={streamViews}
        pagination={pagination}
        onPaginationChange={handleSetPagination}
      />
    </StatisticsCard>
  )
}
